import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from 'src/app/core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { ModuleService } from '../../services/module/module.service';
import * as moduleActions from './module.actions';
import * as moduleItemActions from '../module-item/module-item.actions';
import { AppState } from '../../../state';
import { ModuleItemService } from '../../services/module-item/module-item.service';

@Injectable()
export class ModuleEffects {
  getModulesInSelectedIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleActions.getModulesInSelectedIntervention),
      switchMap(({ studyId, interventionId }) => {
        return this.moduleService.getModulesInSelectedIntervention(studyId, interventionId).pipe(
          map((modules) => {
            const transformedModules = this.dashboardCamelCasePipe.keysToCamelCase(modules);
            return moduleActions.getModulesInSelectedInterventionSuccess({ modules: transformedModules });
          }),
          catchError((err: HttpErrorResponse) => of(moduleActions.getModulesInSelectedInterventionError({ err })))
        );
      })
    )
  );

  getModulesInSelectedInterventionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleActions.getModulesInSelectedInterventionError),
        tap(({ err }) => this.toastrService.error('Unable to load modules in selected intervention'))
      ),
    { dispatch: false }
  );

  createModule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleActions.createModule),
      switchMap(({ module }) => {
        const moduleSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(module);
        return this.moduleService.createModule(moduleSnakeCase).pipe(
          map((newModule) =>
            moduleActions.createModuleSuccess({ newModule: this.dashboardCamelCasePipe.keysToCamelCase(newModule) })
          ),
          catchError((err: HttpErrorResponse) => of(moduleActions.createModuleError({ err })))
        );
      })
    )
  );

  createModuleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleActions.createModuleSuccess),
      map(({ newModule }) => {
        this.toastrService.success(`Module ${newModule?.translations[0]?.translation} created!`);
        return moduleItemActions.setSelectedModuleItems({ moduleItems: newModule?.moduleItems });
      })
    )
  );

  createModuleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleActions.createModuleError),
        tap(({ err }) => this.toastrService.error(`${err.error.error}`, 'Unable to create module'))
      ),
    { dispatch: false }
  );

  getModule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleActions.getModule),
      switchMap(({ studyId, moduleId }) => {
        return this.moduleService.getModuleById(studyId, moduleId).pipe(
          map((selectedModule) =>
            moduleActions.getModuleSuccess({
              selectedModule: {
                ...this.dashboardCamelCasePipe.keysToCamelCase(selectedModule),
              },
            })
          ),
          catchError((err: HttpErrorResponse) => of(moduleActions.getModuleError({ err })))
        );
      })
    )
  );

  getModuleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleActions.getModuleSuccess),
      map(({ selectedModule }) =>
        moduleItemActions.setSelectedModuleItems({ moduleItems: selectedModule?.moduleItems })
      )
    )
  );

  updateModule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleActions.updateModule),
      switchMap(({ module }) => {
        const moduleSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(module);
        return this.moduleService.updateModule(moduleSnakeCase).pipe(
          map((updatedModule) =>
            moduleActions.updateModuleSuccess({
              updatedModule: this.dashboardCamelCasePipe.keysToCamelCase(updatedModule),
            })
          ),
          catchError((err: HttpErrorResponse) => of(moduleActions.updateModuleError({ err })))
        );
      })
    )
  );

  updateModuleSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleActions.updateModuleSuccess),
        tap(({ updatedModule }) =>
          this.toastrService.success(`Module ${updatedModule?.translations[0]?.translation} updated!`)
        )
      ),
    { dispatch: false }
  );

  updateModuleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleActions.updateModuleError),
        tap(({ err }) => this.toastrService.error(`${err.error.error}`, 'Unable to update module'))
      ),
    { dispatch: false }
  );

  duplicateModule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleActions.duplicateModule),
      switchMap(({ module }) => {
        const moduleSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(module);
        return this.moduleService.duplicateModule(moduleSnakeCase).pipe(
          map((duplicatedModule) =>
            moduleActions.duplicateModuleSuccess({
              duplicatedModule: this.dashboardCamelCasePipe.keysToCamelCase(duplicatedModule),
            })
          ),
          catchError((err: HttpErrorResponse) => of(moduleActions.duplicateModuleError({ err })))
        );
      })
    )
  );

  duplicateModuleSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleActions.duplicateModuleSuccess),
        tap(({ duplicatedModule }) =>
          this.toastrService.success(
            `Module ${duplicatedModule?.translations[0]?.translation} duplicated! Please add calculation names in module items that require it`
          )
        )
      ),
    { dispatch: false }
  );

  duplicateModuleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleActions.duplicateModuleError),
        tap(({ err }) => this.toastrService.error(`${err.error.error}`, 'Unable to duplicate module'))
      ),
    { dispatch: false }
  );

  deleteModule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleActions.deleteModule),
      switchMap(({ module }) => {
        const moduleSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(module);
        return this.moduleService.deleteModule(moduleSnakeCase).pipe(
          map(() => moduleActions.deleteModuleSuccess({ deletedModule: module })),
          catchError((err: HttpErrorResponse) => of(moduleActions.deleteModuleError({ err })))
        );
      })
    )
  );

  deleteModuleSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleActions.deleteModuleSuccess),
        tap(({ deletedModule }) =>
          this.toastrService.success(`Module ${deletedModule?.translations[0]?.translation} deleted!`)
        )
      ),
    { dispatch: false }
  );

  deleteModuleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleActions.deleteModuleError),
        tap(({ err }) => this.toastrService.error(`${err.error.error}`, 'Unable to delete module'))
      ),
    { dispatch: false }
  );

  exportModuleToCSV$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleActions.exportModuleToCSV),
        switchMap(({ module }) => {
          return this.moduleService.exportModuleToCSV(module).pipe(
            tap((result: any) => {
              const anchor = document.createElement('a');
              anchor.download = `module-${module.name}-items-export.csv`;
              anchor.href = window.URL.createObjectURL(new Blob([result], { type: 'text/csv' }));
              document.body.appendChild(anchor);
              anchor.click();
              document.body.removeChild(anchor);
            })
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe,
    private moduleService: ModuleService,
    private moduleItemService: ModuleItemService,
    private toastrService: ToastrService
  ) {}
}
